import localforage from 'localforage';

export type AuthRole = 'staff' | 'patient' | 'provider';

export type AuthUser = {
    role: AuthRole;
    email: string;
    full_name: string;
    fname: string;
    lname: string;
    staff_id?: number;
    clinic_id?: number;
    institution_id?: number;
    clinic_name?: string | null;
    institution?: string | null;
    patient_id?: string;
    preferences?: any;
    provider_id?: number;
    specialization?: string;
    verification_status?: string;
};

export type AuthSessionResult = {
    user: AuthUser;
    accessToken: string;
    expiresAt: string;
};

const buildApiUrl = (baseUrl: string, path: string) => {
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return new URL(normalizedPath, normalizedBaseUrl).toString();
};

const decodeJwtExpiry = (token: string) => {
    try {
        const payload = token.split('.')[1];
        if (!payload) return 0;
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        const parsed = JSON.parse(window.atob(normalized));
        return typeof parsed.exp === 'number' ? parsed.exp * 1000 : 0;
    } catch {
        return 0;
    }
};

const isTokenUsable = (token: string) => {
    const expiresAt = decodeJwtExpiry(token);
    return !!token && !!expiresAt && expiresAt > Date.now() + 60_000;
};

const readStoredToken = async () => {
    const sessionToken = sessionStorage.getItem('access_token') || sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
    if (sessionToken && isTokenUsable(sessionToken)) return sessionToken;

    const localToken = localStorage.getItem('access_token') || localStorage.getItem('token') || localStorage.getItem('authToken');
    if (localToken && isTokenUsable(localToken)) return localToken;

    const persistedToken = await localforage.getItem('access_token');
    if (typeof persistedToken === 'string' && isTokenUsable(persistedToken)) return persistedToken;

    return '';
};

const persistToken = async (token: string) => {
    sessionStorage.setItem('access_token', token);
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('authToken', token);
    sessionStorage.setItem('user_token', token);
    localStorage.setItem('access_token', token);
    localStorage.setItem('token', token);
    localStorage.setItem('authToken', token);
    localStorage.setItem('user_token', token);
    await localforage.setItem('access_token', token);
    await localforage.setItem('token', token);
    await localforage.setItem('user_token', token);
};

export const clearLegacyAuthStorage = async () => {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user_token');
    localStorage.removeItem('patient_token');
    localStorage.removeItem('patient_id');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_role');
    localStorage.removeItem('clinic_id');
    localStorage.removeItem('institution_id');
    localStorage.removeItem('staff_details');
    localStorage.removeItem('uzima_login_info');
    await localforage.removeItem('access_token');
    await localforage.removeItem('token');
    await localforage.removeItem('user_token');
    await localforage.removeItem('patient_token');
    await localforage.removeItem('patient_id');
    await localforage.removeItem('patientId');
    await localforage.removeItem('staff_id');
    await localforage.removeItem('clinic_id');
    await localforage.removeItem('institution_id');
    await localforage.removeItem('user_id');
    await localforage.removeItem('user_role');
    await localforage.removeItem('login_status');
    await localforage.removeItem('is_authenticated');
};

export const persistLegacyAuthState = async (user: AuthUser, accessToken: string) => {
    await persistToken(accessToken);
    await localforage.setItem('is_authenticated', true);
    if (user.role === 'staff') {
        const loginInfo = {
            staff_id: user.staff_id,
            fname: user.fname,
            lname: user.lname,
            institution: user.institution,
            institution_id: user.institution_id,
            clinic_id: user.clinic_id,
            email: user.email,
            token: accessToken,
            login_time: new Date().toISOString(),
            user_type: 'staff',
            status: 'success',
        };
        await localforage.setItem('uzima_login_info', loginInfo);
        await localforage.setItem('staff_id', user.staff_id ?? '');
        await localforage.setItem('clinic_id', user.clinic_id ?? '');
        await localforage.setItem('institution_id', user.institution_id ?? '');
        await localforage.setItem('user_id', user.staff_id ?? '');
        await localforage.setItem('user_role', 'staff');
        await localforage.setItem('staff_fname', user.fname);
        await localforage.setItem('staff_lname', user.lname);
        await localforage.setItem('institution_name', user.institution ?? '');
        await localforage.setItem('user_email', user.email);
        await localforage.setItem('fname', user.fname);
        await localforage.setItem('lname', user.lname);
        await localforage.setItem('clinic_name', user.clinic_name || user.institution || '');
        await localforage.setItem('user_avatar', 'avatar.png');
        await localforage.setItem('login_status', 'success');
        localStorage.setItem('uzima_login_info', JSON.stringify(loginInfo));
        localStorage.setItem('user_id', String(user.staff_id ?? ''));
        localStorage.setItem('user_role', 'staff');
        localStorage.setItem('clinic_id', String(user.clinic_id ?? ''));
        localStorage.setItem('institution_id', String(user.institution_id ?? ''));
        localStorage.setItem('staff_details', JSON.stringify({
            fname: user.fname,
            lname: user.lname,
            full_name: user.full_name,
            email: user.email,
            staff_id: user.staff_id,
            clinic_id: user.clinic_id,
            institution: user.institution,
            institution_id: user.institution_id,
        }));
        return;
    }

    if (user.role === 'provider') {
        // Previously absent - this codebase's role check was `if staff {...}
        // else {assume patient}`, so a provider login would have silently
        // fallen through and been stored as patient data (undefined
        // patient_id, wrong user_role), breaking anything downstream that
        // reads localforage/localStorage for the logged-in identity.
        const providerData = {
            first_name: user.fname,
            last_name: user.lname,
            email: user.email,
            specialization: user.specialization,
            verification_status: user.verification_status,
        };
        await localforage.setItem('provider_id', user.provider_id ?? '');
        await localforage.setItem('user_id', user.provider_id ?? '');
        await localforage.setItem('user_role', 'provider');
        await localforage.setItem('provider_data', JSON.stringify(providerData));
        localStorage.setItem('provider_id', String(user.provider_id ?? ''));
        localStorage.setItem('user_id', String(user.provider_id ?? ''));
        localStorage.setItem('user_role', 'provider');
        localStorage.setItem('provider_data', JSON.stringify(providerData));
        return;
    }

    const patientData = {
        first_name: user.fname,
        last_name: user.lname,
        email: user.email,
        preferences: user.preferences,
    };
    await localforage.setItem('patient_id', user.patient_id ?? '');
    await localforage.setItem('patientId', user.patient_id ?? '');
    await localforage.setItem('patient_token', accessToken);
    await localforage.setItem('user_id', user.patient_id ?? '');
    await localforage.setItem('user_role', 'patient');
    await localforage.setItem('patient_data', JSON.stringify(patientData));
    localStorage.setItem('patient_id', user.patient_id ?? '');
    localStorage.setItem('patient_token', accessToken);
    localStorage.setItem('user_id', user.patient_id ?? '');
    localStorage.setItem('user_role', 'patient');
    localStorage.setItem('patient_data', JSON.stringify(patientData));
};

const readJsonResponse = async (response: Response, fallbackMessage: string) => {
    const contentType = response.headers.get('content-type') || '';
    const responseText = await response.text();

    if (!contentType.toLowerCase().includes('application/json')) {
        const preview = responseText.slice(0, 120).replace(/\s+/g, ' ').trim();
        throw new Error(`${fallbackMessage} Received ${contentType || 'non-JSON response'} from ${response.url}.${preview ? ` Preview: ${preview}` : ''}`);
    }

    try {
        return responseText ? JSON.parse(responseText) : null;
    } catch {
        throw new Error(`${fallbackMessage} Invalid JSON response from ${response.url}`);
    }
};

const fetchCsrfToken = async (baseUrl: string) => {
    const response = await fetch(buildApiUrl(baseUrl, 'tok'), {
        method: 'GET',
        credentials: 'include',
    });
    if (!response.ok) {
        throw new Error('Unable to establish CSRF session');
    }
    const payload = await readJsonResponse(response, 'Unable to establish CSRF session.');
    return payload?.message || '';
};

export const restoreModernSession = async (baseUrl: string): Promise<AuthSessionResult | null> => {
    const response = await fetch(buildApiUrl(baseUrl, 'core/api/auth/session/'), {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        await clearLegacyAuthStorage();
        return null;
    }

    const payload = await readJsonResponse(response, 'Unable to restore authenticated session.');
    const data = payload?.data;
    if (!data?.user || !data?.access_token) {
        await clearLegacyAuthStorage();
        return null;
    }

    await persistLegacyAuthState(data.user, data.access_token);
    return {
        user: data.user,
        accessToken: data.access_token,
        expiresAt: data.expires_at,
    };
};

export const loginWithModernAuth = async (baseUrl: string, role: AuthRole, email: string, password: string): Promise<AuthSessionResult> => {
    const csrfToken = await fetchCsrfToken(baseUrl);
    const response = await fetch(buildApiUrl(baseUrl, 'core/api/auth/login/'), {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({ role, email, password }),
    });

    const payload = await readJsonResponse(response, 'Login request failed.');
    if (!response.ok || payload?.status !== 'success' || !payload?.data?.user || !payload?.data?.access_token) {
        throw new Error(payload?.message || 'Login failed');
    }

    await persistLegacyAuthState(payload.data.user, payload.data.access_token);
    return {
        user: payload.data.user,
        accessToken: payload.data.access_token,
        expiresAt: payload.data.expires_at,
    };
};

export const logoutModernAuth = async (baseUrl: string) => {
    try {
        const csrfToken = await fetchCsrfToken(baseUrl);
        await fetch(buildApiUrl(baseUrl, 'core/api/auth/logout/'), {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
        });
    } finally {
        await clearLegacyAuthStorage();
    }
};

export const getCompatibleAccessToken = async (baseUrl: string) => {
    const existingToken = await readStoredToken();
    if (existingToken) {
        return existingToken;
    }

    const restoredSession = await restoreModernSession(baseUrl);
    return restoredSession?.accessToken || '';
};
